import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { format } from 'date-fns';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { Shimmer } from 'office-ui-fabric-react/lib/Shimmer';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Label } from 'office-ui-fabric-react/lib/Label';
import Module from '../../components/Module';
import ModuleContent from '../ModuleContent';
import ModuleHeader from '../ModuleHeader';
import StackGrid from '../StackGrid';
import styles from './TransactionsStats.style';
import { formatCurrency } from '../../utils';

const TransactionsStats = ({
  totalTransactionsQuantity,
  currency,
  statementDates,
  totalEarnedRoyalty,
  noTransactionDataMessage,
  hideDates,
}) => {
  return (
    <Module className={styles.root}>
      {totalTransactionsQuantity > 0 && (
        <>
          <ModuleContent>
            <ModuleHeader className={styles.moduleHeader}>Current Statement Period</ModuleHeader>
            <div className={styles.root}>
              <div className={styles.moduleContent}>
                {!hideDates && (
                  <StackGrid
                    className={clsx(styles.statementRow, styles.moduleIndentedContent)}
                    smCols={1}
                    mdCols={1}
                    lgCols={1}
                    shrink
                  >
                    <div>
                      <Label
                        htmlFor="statementPeriod"
                        className={clsx(styles.headingTextLabel, styles.headingTextLabelTooltip)}
                      >
                        Dates
                      </Label>
                      <Shimmer
                        isDataLoaded={statementDates && statementDates.start && statementDates.end}
                      >
                        <Text id="statementPeriod" className={styles.headingText}>
                          {statementDates &&
                            `${format(new Date(statementDates.start), 'MMM dd, yyyy')} - Today`}
                        </Text>
                      </Shimmer>
                    </div>
                  </StackGrid>
                )}
                <StackGrid
                  className={styles.moduleIndentedContent}
                  smCols={1}
                  mdCols={1}
                  lgCols={2}
                  shrink
                >
                  <div>
                    <Label htmlFor="royaltiesEarned" className={styles.headingTextLabel}>
                      Royalties Earned
                    </Label>
                    <Shimmer
                      isDataLoaded={totalEarnedRoyalty !== undefined && totalEarnedRoyalty !== null}
                    >
                      <Text id="royaltiesEarned" className={styles.headingText}>
                        {totalEarnedRoyalty &&
                          currency &&
                          `${
                            formatCurrency(totalEarnedRoyalty, currency, {
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            })
                              .replace(/\D/g, '') // remove currency symbol
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ',') // format commas back in
                          } ${currency}`}
                      </Text>
                    </Shimmer>
                  </div>
                  <div>
                    <Label htmlFor="transactionsProcessed" className={styles.headingTextLabel}>
                      Transactions Processed
                    </Label>
                    <Shimmer
                      isDataLoaded={
                        totalTransactionsQuantity !== undefined &&
                        totalTransactionsQuantity !== null
                      }
                    >
                      <Text id="transactionsProcessed" className={styles.headingText}>
                        {totalTransactionsQuantity && totalTransactionsQuantity.toLocaleString()}
                      </Text>
                    </Shimmer>
                  </div>
                </StackGrid>
              </div>
            </div>
          </ModuleContent>
          <div className={styles.infoMessage}>
            <FontIcon
              iconName="Info"
              ariaLabel="Info"
              id="select-partner-icon"
              className={styles.infoIcon}
            />
            <Text as="p" className={styles.infoText}>
              The above data represents accrual for the given date range. The current statement
              period ends on{' '}
              {statementDates &&
                statementDates.end &&
                format(new Date(statementDates.end), 'MMM dd, yyyy')}
              .<br />
              Transactions are not final until the statement is released.
            </Text>
          </div>
        </>
      )}
      {totalTransactionsQuantity === 0 && (
        <MessageBar isMultiline className={styles.messageBar}>
          <Text className={styles.noTransactionDataTitle}>No Transaction Data</Text>
          <br />
          <Text className={styles.noTransactionDataMessage}>{noTransactionDataMessage}</Text>
        </MessageBar>
      )}
    </Module>
  );
};

TransactionsStats.propTypes = {
  statementDates: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  totalTransactionsQuantity: PropTypes.number,
  currency: PropTypes.string,
  totalEarnedRoyalty: PropTypes.number,
  noTransactionDataMessage: PropTypes.string,
  hideDates: PropTypes.bool,
};

TransactionsStats.defaultProps = {
  statementDates: null,
  totalTransactionsQuantity: 0,
  currency: null,
  totalEarnedRoyalty: 0,
  noTransactionDataMessage: null,
  hideDates: false,
};

export default TransactionsStats;
